import { useEffect, useRef, useState, type FormEvent } from "react";
import { ExternalLink, Loader2, RefreshCw, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { publicApi } from "@/services/api-client";
import { ErrorBox, KV, Seg, Tag, isAbort, record, safeUrl, text } from "./kit";

export type LookupKind = "payment-intents" | "withdrawals" | "refunds";
const LABEL: Record<LookupKind, string> = {
  "payment-intents": "Dépôt (payment intent)",
  withdrawals: "Retrait",
  refunds: "Remboursement",
};

/**
 * Manual current-state follow-up. One request per explicit click, no polling.
 */
export function OperationLookup({
  initialKind = "payment-intents",
  initialId = "",
  compact,
}: {
  initialKind?: LookupKind;
  initialId?: string;
  compact?: boolean;
}) {
  const [kind, setKind] = useState<LookupKind>(initialKind);
  const [id, setId] = useState(initialId);
  const [result, setResult] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const ctrl = useRef<AbortController | null>(null);
  useEffect(() => () => ctrl.current?.abort(), []);

  async function run(e?: FormEvent) {
    e?.preventDefault();
    const ref = id.trim();
    if (!ref) return;
    ctrl.current?.abort();
    const c = new AbortController();
    ctrl.current = c;
    setBusy(true);
    setError(null);
    try {
      const { data } = await publicApi<Record<string, unknown>>(
        `/${kind}/${encodeURIComponent(ref)}`,
        { signal: c.signal },
      );
      setResult(record(data));
    } catch (err) {
      if (!isAbort(err)) {
        setResult(null);
        setError(err);
      }
    } finally {
      if (ctrl.current === c) setBusy(false);
    }
  }

  const link = safeUrl(
    result &&
      (result.provider_link ?? record(result.payment_intent).provider_link),
  );
  const status =
    result &&
    text(
      result,
      "status",
      "state",
      "payment_intent.status",
      "withdrawal.status",
      "refund.status",
    );

  return (
    <div className="space-y-3" data-testid="operation-lookup">
      {!compact && (
        <Seg
          label="Type d'opération"
          value={kind}
          onChange={(k) => {
            setKind(k);
            setResult(null);
            setError(null);
          }}
          items={Object.entries(LABEL) as [LookupKind, string][]}
        />
      )}
      <form
        onSubmit={run}
        className="flex flex-col gap-2 sm:flex-row sm:items-end"
      >
        <div className="grid flex-1 gap-1.5">
          <Label htmlFor="lookup-id">
            {kind === "payment-intents"
              ? "Identifiant du payment intent"
              : "Référence"}
          </Label>
          <Input
            id="lookup-id"
            value={id}
            onChange={(e) => setId(e.target.value)}
            placeholder={kind === "payment-intents" ? "uuid" : "BCP-..."}
            className="font-mono"
            data-testid="input-lookup-id"
          />
        </div>
        <Button
          type="submit"
          disabled={busy || !id.trim()}
          data-testid="button-lookup"
        >
          {busy ? (
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
          ) : result ? (
            <RefreshCw className="mr-1.5 h-4 w-4" />
          ) : (
            <Search className="mr-1.5 h-4 w-4" />
          )}
          {result ? "Actualiser l'état" : "Consulter l'état"}
        </Button>
      </form>
      <ErrorBox error={error} onRetry={() => void run()} />
      {result && (
        <div className="rounded-lg border bg-muted/30 p-3">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-xs font-semibold text-muted-foreground">
              {LABEL[kind]} · état serveur actuel
            </p>
            {status && <Tag value={status} />}
          </div>
          {link && (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="mb-2 inline-flex items-center gap-1 text-xs font-semibold text-primary underline"
            >
              Ouvrir le lien fournisseur <ExternalLink className="h-3 w-3" />
            </a>
          )}
          <KV data={result} exclude={["provider_link"]} />
        </div>
      )}
    </div>
  );
}
